import React, { ReactElement } from 'react';
import { Icon, Tooltip } from '@dexma/ui-components';
import i18n from 'asset/translation';
import Dashboard, { DashboardAccessVisibility, DashboardUserGroup } from 'domain/entity/dashboard';
import { StyledFlex, StyledSpaceBetween, StyledSpan } from 'adapter/in/common/components/HomeTable/style';

enum AccessType {
    EVERYONE,
    GROUPS,
    SHARED,
    JUST_ME,
}

export const useDashboardAccessColumn = (onCell?: (record: any) => React.TdHTMLAttributes<HTMLElement>) => {
    const accessType = (dashboard: Dashboard): AccessType => {
        const { access: dashboardAccess, owner: dashboardOwner } = dashboard;

        if (
            dashboardAccess.visibility === DashboardAccessVisibility.PUBLIC ||
            dashboardAccess.visibility === DashboardAccessVisibility.GLOBAL
        ) {
            return AccessType.EVERYONE;
        }

        if (dashboardAccess.visibility === DashboardAccessVisibility.PRIVATE && dashboardAccess.groups.length === 0) {
            if (dashboardOwner.me) {
                return AccessType.JUST_ME;
            }

            if (!dashboardOwner.me) {
                return AccessType.SHARED;
            }
        }

        return AccessType.GROUPS;
    };

    const access = (dashboard: Dashboard, render: (text: string, icon: string) => ReactElement) => {
        const { access: dashboardAccess } = dashboard;
        let text: string;
        let icon: string;
        switch (accessType(dashboard)) {
            case AccessType.GROUPS:
                text = dashboardAccess.groups.map((editGroup: DashboardUserGroup) => editGroup.name).join(', ');
                icon = 'people';
                break;
            case AccessType.EVERYONE:
                text = i18n.t('dashboards.everyone');
                icon = 'world';
                break;
            case AccessType.JUST_ME:
                text = i18n.t('dashboards.justMe');
                icon = 'hide';
                break;
            case AccessType.SHARED:
            default:
                text = i18n.t('dashboards.shared');
                icon = 'people';
                break;
        }

        return render(text, icon);
    };

    return {
        column: {
            title: i18n.t('dashboards.access'),
            dataIndex: '',
            width: '25%',
            className: 'remove-border',
            onCell,
            render: (dashboard: Dashboard) =>
                access(dashboard, (text, icon) => (
                    <StyledSpaceBetween>
                        <StyledFlex>
                            <Icon name={icon} ariaLabel='TODO' />
                            <StyledSpan>{text}</StyledSpan>
                        </StyledFlex>
                        {dashboard.pinned && (
                            <Tooltip title={i18n.t('dashboards.pinned.tooltip.info')}>
                                <Icon
                                    name='push_pin'
                                    data-testid='push_pin'
                                    ariaLabel={i18n.t('dashboards.pinned.tooltip.iconAriaLabel')}
                                />
                            </Tooltip>
                        )}
                    </StyledSpaceBetween>
                )),
        },
    };
};
